import React from 'react';
import Card from '../ui/Card';
import { Folder, RefreshCw, CheckCircle, Download, Edit, Trash2, ArrowUpDown, BookOpen, Users, Calendar, Play, Building2 } from 'lucide-react';
import { ProjectTableData } from '../../hooks/useProjectManagement';

interface ProjectListTableProps {
    projects: ProjectTableData[];
    loadingProject: string | null;
    loadSuccess: string | null;
    onLoad: (name: string) => void;
    onExport: (name: string) => void;
    onDelete: (name: string) => void;
    onEdit: (name: string) => void;
    onSort: (field: 'name' | 'customer' | 'modified' | 'stage') => void;
    canDelete: (name: string) => boolean;
    totalBackendProjects: number;
}

export const ProjectListTable: React.FC<ProjectListTableProps> = ({
    projects, loadingProject, loadSuccess, onLoad, onExport, onDelete, onEdit, onSort, canDelete, totalBackendProjects
}) => {

    const getStatusBadge = (status: string) => {
        const colors: Record<string, string> = {
            active: 'bg-green-100 text-green-700',
            inactive: 'bg-gray-100 text-gray-600',
            archived: 'bg-yellow-100 text-yellow-700'
        };
        return colors[status] || colors.active;
    };

    const getStageBadge = (stage: string) => {
        const colors: Record<string, string> = {
            concept: 'bg-purple-100 text-purple-700',
            design: 'bg-blue-100 text-blue-700',
            prototype: 'bg-cyan-100 text-cyan-700',
            production: 'bg-green-100 text-green-700',
            maintenance: 'bg-orange-100 text-orange-700'
        };
        return colors[stage] || colors.design;
    };

    return (
        <Card title="Project Repository">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-b border-border mb-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-primary uppercase tracking-wide">
                            Quick Load:
                        </span>
                        <select
                            className="px-4 py-2.5 h-11 bg-white dark:bg-gray-800 border-2 border-primary/40 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary shadow-md min-w-[280px] cursor-pointer hover:border-primary transition-all"
                            onChange={(e) => {
                                if (e.target.value) {
                                    onLoad(e.target.value);
                                }
                            }}
                            defaultValue=""
                        >
                            <option value="">📁 Select a project to load...</option>
                            {projects.map(p => (
                                <option key={p.name} value={p.name}>
                                    {p.displayName} ({p.fmeaStandard?.replace(/_/g, ' ') || 'AIAG VDA'})
                                </option>
                            ))}
                        </select>
                    </div>
                    <span className="text-sm text-text-secondary">
                        {projects.length} project(s) available
                    </span>
                </div>
                <div className="text-sm text-text-secondary">
                    Click project name in table to load • Actions on right for export/edit
                </div>
            </div>
            <div className="overflow-x-auto min-h-[400px]">
                <table className="w-full text-sm">
                    <thead className="bg-background/50 text-xs uppercase font-bold text-text-secondary border-b-2 border-border">
                        <tr>
                            <th className="px-4 py-3 text-left">
                                <button onClick={() => onSort('name')} className="flex items-center gap-1 hover:text-primary">
                                    Project Name <ArrowUpDown size={12} />
                                </button>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <button onClick={() => onSort('customer')} className="flex items-center gap-1 hover:text-primary">
                                    Customer <ArrowUpDown size={12} />
                                </button>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center gap-1">
                                    <BookOpen size={12} /> FMEA Standard
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-left">
                                <button onClick={() => onSort('stage')} className="flex items-center gap-1 hover:text-primary">
                                    Stage <ArrowUpDown size={12} />
                                </button>
                            </th>
                            <th className="px-4 py-3 text-left">Owner</th>
                            <th className="px-4 py-3 text-left">
                                <button onClick={() => onSort('modified')} className="flex items-center gap-1 hover:text-primary">
                                    Last Modified <ArrowUpDown size={12} />
                                </button>
                            </th>
                            <th className="px-4 py-3 text-right">Items</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map(project => (
                            <tr
                                key={project.id}
                                className={`border-b border-border hover:bg-background/50 transition-colors ${project.isActive ? 'bg-primary/5' : ''}`}
                            >
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        {loadingProject === project.name ? (
                                            <RefreshCw size={16} className="text-primary animate-spin" />
                                        ) : loadSuccess === project.name ? (
                                            <CheckCircle size={16} className="text-emerald-500" />
                                        ) : (
                                            <Folder size={16} className={project.isActive ? "text-primary" : "text-text-secondary"} />
                                        )}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => onLoad(project.name)}
                                                disabled={loadingProject === project.name}
                                                className={`font-medium hover:underline cursor-pointer transition-colors ${loadSuccess === project.name
                                                    ? "text-emerald-600"
                                                    : project.isActive
                                                        ? "text-primary"
                                                        : "text-blue-600 hover:text-blue-800"
                                                    }`}
                                                title="Click to load this project"
                                            >
                                                {loadingProject === project.name ? 'Loading...' : project.displayName}
                                            </button>
                                            {loadSuccess === project.name && (
                                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase rounded-full animate-pulse">
                                                    Loaded!
                                                </span>
                                            )}
                                            {project.isActive && loadSuccess !== project.name && (
                                                <span className="px-2 py-0.5 bg-primary text-white text-[10px] font-bold uppercase rounded-full">
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                        <Building2 size={14} className="text-text-secondary" />
                                        {project.customer}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                        {project.fmeaStandard?.replace(/_/g, ' ').replace('26262', ' 26262').replace('61508', ' 61508') || 'AIAG VDA'}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${getStatusBadge(project.status)}`}>
                                        {project.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${getStageBadge(project.stage)}`}>
                                        {project.stage}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                        <Users size={14} className="text-text-secondary" />
                                        {project.owner}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1 text-xs text-text-secondary">
                                        <Calendar size={14} />
                                        {new Date(project.lastModified).toLocaleDateString()}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-sm">
                                    {project.itemCount}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-1">
                                        {!project.isActive && (
                                            <button
                                                onClick={() => onLoad(project.name)}
                                                className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"
                                                title="Load Project"
                                            >
                                                <Play size={14} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onExport(project.name)}
                                            className="p-1.5 hover:bg-muted rounded text-text-secondary"
                                            title="Export JSON"
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button
                                            onClick={() => onEdit(project.name)}
                                            className="p-1.5 hover:bg-yellow-50 text-yellow-600 rounded"
                                            title="Edit Metadata"
                                        >
                                            <Edit size={14} />
                                        </button>
                                        {canDelete(project.name) && (
                                            <button
                                                onClick={() => onDelete(project.name)}
                                                className="p-1.5 hover:bg-red-50 text-red-500 rounded"
                                                title="Delete Project"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {projects.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center">
                                    <div className="text-text-secondary">
                                        {totalBackendProjects > 0 ? (
                                            <p className="italic">No projects match your filters. Try adjusting the search or filters.</p>
                                        ) : (
                                            <div>
                                                <p className="italic mb-2 text-lg">No projects found in backend repository.</p>
                                                <p className="text-sm">Backend projects: {totalBackendProjects}</p>
                                                <p className="text-sm mt-2">Create a new project or import JSON to get started.</p>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
